import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Warehouse, Plus, Edit2, Trash2, X, Search, MapPin, Phone, User, Download } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

const LOCATION_TYPES = ["WAREHOUSE", "OFFICE", "FACTORY", "STORE", "HOME"];
const LOCATION_TYPE_BADGE = {
  WAREHOUSE: "bg-blue-100 text-blue-700 border-blue-200",
  OFFICE: "bg-slate-100 text-slate-700 border-slate-200",
  FACTORY: "bg-amber-100 text-amber-700 border-amber-200",
  STORE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  HOME: "bg-purple-100 text-purple-700 border-purple-200",
};

export default function WarehouseManagement() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState({
    name: "", address: "", address_line2: "", city: "", state: "", pincode: "",
    contact_person: "", contact_phone: "",
    location_type: "WAREHOUSE", is_default: false,
  });

  useEffect(() => { fetchWarehouses(); }, []);

  const fetchWarehouses = async () => {
    try {
      const res = await api.get("/warehouses");
      setWarehouses(res.data || []);
    } catch { toast.error("Failed to load warehouses"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.address || !form.city || !form.pincode) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      if (editingWarehouse) {
        await api.put(`/warehouses/${editingWarehouse.id}`, form);
        toast.success("Warehouse updated!");
      } else {
        const res = await api.post("/warehouses", form);
        toast.success(`Warehouse created! Code: ${res.data.code}`);
      }
      setShowModal(false);
      setEditingWarehouse(null);
      setForm({
        name: "", address: "", address_line2: "", city: "", state: "", pincode: "",
        contact_person: "", contact_phone: "",
        location_type: "WAREHOUSE", is_default: false,
      });
      fetchWarehouses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (wh) => {
    setEditingWarehouse(wh);
    setForm({
      name: wh.name, address: wh.address,
      address_line2: wh.address_line2 || "",
      city: wh.city, state: wh.state || "", pincode: wh.pincode,
      contact_person: wh.contact_person || "", contact_phone: wh.contact_phone || "",
      location_type: wh.location_type || "WAREHOUSE",
      is_default: !!wh.is_default,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this warehouse?")) return;
    try {
      await api.delete(`/warehouses/${id}`);
      toast.success("Warehouse deleted!");
      fetchWarehouses();
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggleStatus = async (wh) => {
    try {
      await api.put(`/warehouses/${wh.id}`, { is_active: !wh.is_active });
      toast.success(`Warehouse ${wh.is_active ? "deactivated" : "activated"}!`);
      fetchWarehouses();
    } catch { toast.error("Failed to update status"); }
  };

  const exportCSV = () => {
    const headers = ["Code", "Name", "Address", "City", "Pincode", "Contact Person", "Contact Phone", "Status"];
    const rows = warehouses.map(w => [
      w.code, w.name, w.address, w.city, w.pincode, w.contact_person || "", w.contact_phone || "", w.is_active ? "Active" : "Inactive"
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "warehouses.csv"; a.click();
  };

  const filtered = warehouses.filter(w =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Pickup Locations</h1>
            <p className="text-slate-500 text-sm mt-0.5">Saved pickup addresses for customers and partners</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 rounded-sm text-sm font-semibold hover:bg-slate-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={() => { setEditingWarehouse(null); setForm({ name: "", address: "", address_line2: "", city: "", state: "", pincode: "", contact_person: "", contact_phone: "", location_type: "WAREHOUSE", is_default: false }); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-sm text-sm font-semibold hover:bg-brand-600"
              data-testid="add-warehouse-btn">
              <Plus className="w-4 h-4" /> Add Pickup Location
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by code, name, or city..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
            data-testid="warehouse-search"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <p className="text-xs font-semibold text-slate-400 uppercase">Total Warehouses</p>
            <p className="text-2xl font-heading font-bold text-slate-800">{warehouses.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <p className="text-xs font-semibold text-slate-400 uppercase">Active</p>
            <p className="text-2xl font-heading font-bold text-emerald-600">{warehouses.filter(w => w.is_active).length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <p className="text-xs font-semibold text-slate-400 uppercase">Inactive</p>
            <p className="text-2xl font-heading font-bold text-red-600">{warehouses.filter(w => !w.is_active).length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <p className="text-xs font-semibold text-slate-400 uppercase">Cities Covered</p>
            <p className="text-2xl font-heading font-bold text-brand-500">{new Set(warehouses.map(w => w.city)).size}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Address</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">City/PIN</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-400">No pickup locations found</td></tr>
                ) : filtered.map((wh) => (
                  <motion.tr key={wh.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-bold text-brand-500">{wh.code}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {wh.name}
                      {wh.is_default && <span className="ml-1 text-[9px] uppercase font-bold text-amber-600">★ default</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${LOCATION_TYPE_BADGE[wh.location_type] || LOCATION_TYPE_BADGE.WAREHOUSE}`}>
                        {wh.location_type || "WAREHOUSE"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{wh.address}</td>
                    <td className="px-4 py-3">
                      <span className="text-slate-800">{wh.city}</span>
                      <span className="text-slate-400 ml-1">- {wh.pincode}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {wh.contact_person && <div className="flex items-center gap-1"><User className="w-3 h-3" /> {wh.contact_person}</div>}
                      {wh.contact_phone && <div className="flex items-center gap-1 text-xs text-slate-400"><Phone className="w-3 h-3" /> {wh.contact_phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggleStatus(wh)}
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${wh.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {wh.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEdit(wh)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit">
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </button>
                        <button onClick={() => handleDelete(wh.id)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg w-full max-w-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-lg">{editingWarehouse ? "Edit Pickup Location" : "Add New Pickup Location"}</h2>
                  <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Warehouse Name *</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g., North Zone Hub"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Address *</label>
                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Full street address"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">City *</label>
                      <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="City"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Pincode *</label>
                      <input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                        placeholder="6-digit PIN"
                        maxLength={6}
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Contact Person</label>
                      <input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                        placeholder="Contact name"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Contact Phone</label>
                      <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                        placeholder="+91 XXXXXXXXXX"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Location Type</label>
                      <select value={form.location_type}
                        onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                        data-testid="location-type-select"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500">
                        {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.is_default}
                          data-testid="location-default-toggle"
                          onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                        Set as default
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-600 rounded-sm text-sm font-semibold hover:bg-slate-50">
                    Cancel
                  </button>
                  <button onClick={handleSubmit}
                    className="px-4 py-2 bg-brand-500 text-white rounded-sm text-sm font-semibold hover:bg-brand-600">
                    {editingWarehouse ? "Update" : "Create"} Warehouse
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
